import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardFilters from './dashboard/DashboardFilters';
import TotalSpendOverview from './dashboard/TotalSpendOverview';
import SpendOverTimeChart from './dashboard/SpendOverTimeChart';
import MonthlySpendBreakdownChart from './dashboard/MonthlySpendBreakdownChart';
import SpendByUserChart from './dashboard/SpendByUserChart';
import SpendByVendorChart from './dashboard/SpendByVendorChart';
import SpendByGLAccountChart from './dashboard/SpendByGLAccountChart';
import SpendByLocationChart from './dashboard/SpendByLocationChart';
import { Loader } from 'lucide-react';

const AdminExpenseDashboard = () => {
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter states
  const [dateRange, setDateRange] = useState('last30days');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedGLAccount, setSelectedGLAccount] = useState('');

  // Dropdown options
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [allTransactions, dateRange, selectedUser, selectedVendor, selectedGLAccount]);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch all data in parallel
      const [transactionsRes, usersRes, vendorsRes, glAccountsRes] = await Promise.all([
        axios.get('/api/statements/transactions/all'),
        axios.get('/api/admin/users'),
        axios.get('/api/vendors?limit=1000'), // Get all vendors (high limit)
        axios.get('/api/gl-accounts?limit=1000') // Get all GL accounts (high limit)
      ]);

      setAllTransactions(transactionsRes.data || []);
      setUsers(usersRes.data || []); // users endpoint returns array directly
      setVendors(vendorsRes.data.vendors || []); // vendors endpoint returns { vendors: [...] }
      setGlAccounts(glAccountsRes.data.glAccounts || []); // glAccounts endpoint returns { glAccounts: [...] }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allTransactions];

    // Apply date range filter
    const now = new Date();
    let startDate;

    switch (dateRange) {
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'thisYear':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'allTime':
      default:
        startDate = null;
        break;
    }

    if (startDate) {
      filtered = filtered.filter(txn => new Date(txn.transactionDate) >= startDate);
    }

    // Apply user filter
    if (selectedUser) {
      filtered = filtered.filter(txn => {
        const txnUserId = txn.userId || txn.user?.id;
        return String(txnUserId) === String(selectedUser);
      });
    }

    // Apply vendor filter
    if (selectedVendor) {
      filtered = filtered.filter(txn => {
        const txnVendorId = txn.vendorId || txn.vendor?.id;
        return String(txnVendorId) === String(selectedVendor);
      });
    }

    // Apply GL account filter
    if (selectedGLAccount) {
      filtered = filtered.filter(txn => {
        const txnGLAccountId = txn.glAccountId || txn.glAccount?.id;
        return String(txnGLAccountId) === String(selectedGLAccount);
      });
    }

    setFilteredTransactions(filtered);
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'last30days':
        return 'Last 30 Days';
      case 'last90days':
        return 'Last 90 Days';
      case 'thisYear':
        return 'This Year';
      case 'allTime':
        return 'All Time';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="h-12 w-12 text-primary-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchAllData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Expense Dashboard</h1>
        <p className="text-sm text-gray-500">
          Showing {filteredTransactions.length} of {allTransactions.length} transactions
        </p>
      </div>

      {/* Filters */}
      <DashboardFilters
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        selectedUser={selectedUser}
        onUserChange={setSelectedUser}
        selectedVendor={selectedVendor}
        onVendorChange={setSelectedVendor}
        selectedGLAccount={selectedGLAccount}
        onGLAccountChange={setSelectedGLAccount}
        users={users}
        vendors={vendors}
        glAccounts={glAccounts}
      />

      {/* KPI Cards */}
      <TotalSpendOverview
        transactions={filteredTransactions}
        dateRangeLabel={getDateRangeLabel()}
      />

      {/* Charts Row 1: Spend Over Time + Monthly Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SpendOverTimeChart transactions={filteredTransactions} granularity="daily" />
        <MonthlySpendBreakdownChart transactions={filteredTransactions} />
      </div>

      {/* Charts Row 2: Spend by User + Spend by Vendor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SpendByUserChart transactions={filteredTransactions} />
        <SpendByVendorChart transactions={filteredTransactions} />
      </div>

      {/* Charts Row 3: Spend by GL Account + Spend by Location */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SpendByGLAccountChart transactions={filteredTransactions} />
        <SpendByLocationChart transactions={filteredTransactions} />
      </div>
    </div>
  );
};

export default AdminExpenseDashboard;
